import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import Button from '../shared/Button';
import FloatingCard from './FloatingCard';
import './HeroSection.css';

const HeroSection = ({ featuredGames, onGameExplore, onBetaAccess }) => {
  const handleSkipToContent = () => {
    document.getElementById('main-content')?.focus();
  };

  return (
    <section className="hero-section" role="banner">
      {/* Skip Link for Accessibility */}
      <button 
        className="skip-link"
        onClick={handleSkipToContent}
        aria-label="Skip to main content"
      >
        Skip to content
      </button>

      <div className="hero-container">
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-text"
          >
            <h1 className="hero-headline">
              Gaming, <span className="gradient-text">Unleashed</span>.
            </h1>
            <p className="hero-subheadline">
              Empowering people with disabilities through adaptive gaming technology
            </p>
            
            <div className="hero-buttons" role="group" aria-label="Main actions">
              <Button
                variant="gradient"
                size="large"
                onClick={onBetaAccess}
                ariaLabel="Access beta portal for enAble Games"
              >
                Access Beta Portal
              </Button>
              <Button
                variant="secondary"
                size="large"
                onClick={onGameExplore}
                ariaLabel="Explore our game collection"
              >
                Explore Games
              </Button>
            </div>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="floating-cards-container" role="img" aria-label="Featured game previews">
              {featuredGames.slice(0, 3).map((game, index) => (
                <FloatingCard
                  key={game._id || `game-${index}`}
                  game={game}
                  index={index}
                  onClick={() => onGameExplore?.(game)}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Animated Background Elements */}
      <div className="hero-bg-elements" aria-hidden="true">
        <div className="gradient-orb gradient-orb--1"></div>
        <div className="gradient-orb gradient-orb--2"></div>
        <div className="gradient-orb gradient-orb--3"></div>
      </div>
    </section>
  );
};

HeroSection.propTypes = {
  featuredGames: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    image: PropTypes.arrayOf(PropTypes.shape({
      uri: PropTypes.string
    })),
    legacyData: PropTypes.shape({
      category: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string)
    })
  })).isRequired,
  onGameExplore: PropTypes.func,
  onBetaAccess: PropTypes.func
};

HeroSection.defaultProps = {
  onGameExplore: () => {},
  onBetaAccess: () => {}
};

export default HeroSection;